// ─── PUBLIC LEDGER ────────────────────────────────────────────────────────────
// /ledger/:handle — readable by anyone, no auth, no account.
//
// ⚠⚠ THE OPEN GRAPH TAGS BELOW DO NOT PRODUCE LINK PREVIEWS. X, LinkedIn and
// Facebook crawlers do not execute JavaScript, so tags written into <head> after
// mount are invisible to them; they read the static index.html and render a bare
// link. They are set anyway because they cost nothing and they DO drive the
// browser tab title and anything that scrapes a rendered page. Real previews
// need per-handle tag injection at the edge — a Cloudflare Function — which is
// its own piece of infrastructure and deliberately not faked here.
//
// ⚠ SignalRow is imported from the desk rather than reimplemented. The claim
// this page makes is that it shows the same record the author sees, and two
// copies of that row would drift the first time either changed.

import { useState, useEffect, useCallback } from "react";
import { C, s, font, backendFetch, NousIcon } from "../core";
import { SignalRow, PhysicalWorldPanel } from "../ThesisDesk";

const REFRESH_MS = 60_000;

const SCORE_COLOR = {
  "STRONGLY ALIGNED": C.green,
  ALIGNED: C.green,
  MIXED: C.orange,
  CHALLENGED: C.red,
  "STRONGLY CHALLENGED": C.red,
  WATCHING: C.textDim,
};

const SECTOR_ICONS = {
  Technology: "💻", Semiconductors: "⚡", Energy: "🛢", Financials: "🏦",
  Healthcare: "🧬", Industrials: "⚙️", Consumer: "🛍", Macro: "🌐",
  "Crypto-adjacent": "₿", Defense: "🛡",
};

function longDate(iso) {
  const ms = Date.parse(iso || "");
  if (!Number.isFinite(ms)) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", month: "long", day: "numeric", year: "numeric",
    }).format(new Date(ms));
  } catch { return "—"; }
}

// ⚠ Replaces the tag if it already exists rather than appending a second one —
// this runs again on every refresh and duplicated og: tags are worse than none.
function setMeta(property, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: font,
                  color: C.text, padding: "24px 16px 64px" }}>
      <div style={{ maxWidth: 780, margin: "0 auto" }}>{children}</div>
    </div>
  );
}

function Cta() {
  return (
    <div style={{ marginTop: 26, textAlign: "center" }}>
      <div style={{ color: C.textDim, marginBottom: 10 }}>Build your own thesis →</div>
      <a href="https://quant-signal.ai" style={{ textDecoration: "none" }}>
        <span style={{ ...s.btn("green"), display: "inline-block", minHeight: 44,
                       padding: "12px 22px" }}>
          GET STARTED — QUANT-SIGNAL.AI
        </span>
      </a>
    </div>
  );
}

export function PublicLedger({ handle }) {
  const [state, setState] = useState({ loading: true, data: null, missing: false });

  const load = useCallback(async (quiet) => {
    if (!quiet) setState((st) => ({ ...st, loading: true }));
    try {
      const p = `/api/thesis/public/${encodeURIComponent(handle)}`;
      const r = await backendFetch(p, p);
      if (r.status === 404) {
        setState({ loading: false, data: null, missing: true });
        return;
      }
      if (!r.ok) {
        // ⚠ An unreachable backend is NOT "no such ledger". Showing 404 here
        // would tell a visitor the author's page does not exist.
        setState((st) => ({ ...st, loading: false }));
        return;
      }
      setState({ loading: false, data: await r.json(), missing: false });
    } catch {
      setState((st) => ({ ...st, loading: false }));
    }
  }, [handle]);

  useEffect(() => { load(); }, [load]);

  // ⚠ The public ledger is live. Followers watch outcomes resolve here, so the
  // page refreshes itself rather than going stale behind a reader who left it
  // open. Quiet refreshes never flip back to the loading state.
  useEffect(() => {
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const d = state.data;
  useEffect(() => {
    if (!d) return;
    const t = d.thesis || {};
    const n = (d.signals || []).length;
    const title = `@${d.handle}'s Alpha Ledger — Quant Signal`;
    const desc = `${t.thesis_name}. ${String(t.direction || "").toUpperCase()} · `
      + `${String(t.style || "").toUpperCase()} · ${String(t.timeframe || "").toUpperCase()}. `
      + `${n} signal${n === 1 ? "" : "s"} fired. Score: ${d.score?.label || "WATCHING"}. `
      + "Platform-verified signal intelligence.";
    document.title = title;
    setMeta("og:title", title);
    setMeta("og:description", desc);
    setMeta("og:url", `https://quant-signal.ai/ledger/@${d.handle}`);
    setMeta("og:type", "profile");
    // ⚠ The platform mark, not a per-thesis render. Generating a real card
    // image needs an edge renderer; pointing at one that does not exist would
    // produce a broken image rather than none.
    setMeta("og:image", "https://quant-signal.ai/favicon.svg");
  }, [d]);

  if (state.loading) {
    return <Shell><div style={{ color: C.textDim }}>Loading ledger…</div></Shell>;
  }

  if (state.missing) {
    return (
      <Shell>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <NousIcon size={48} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.14em",
                          color: C.green }}>QUANT SIGNAL</div>
            <div style={{ fontSize: 10, letterSpacing: "0.22em", color: C.textDim }}>
              THESIS LEDGER
            </div>
          </div>
        </div>
        <div style={{ ...s.panel, padding: 18, lineHeight: 1.7 }}>
          <div style={{ color: C.orange, fontWeight: 700 }}>No public ledger here.</div>
          <div style={{ color: C.textDim, marginTop: 6 }}>
            This handle has no published ledger, or its author has made it private.
          </div>
        </div>
        <Cta />
      </Shell>
    );
  }

  if (!d) {
    return (
      <Shell>
        <div style={{ color: C.orange }}>This ledger is unreachable right now.</div>
        <button style={{ ...s.btn(), minHeight: 44, marginTop: 10 }}
                onClick={() => load()}>RETRY</button>
      </Shell>
    );
  }

  const t = d.thesis || {};
  const signals = d.signals || [];
  const windows = d.windows || [];
  const score = d.score || {};
  const label = score.label || "WATCHING";
  const color = SCORE_COLOR[label] || C.textDim;
  const counts = score.counts || { ALIGNS: 0, CHALLENGES: 0, NEUTRAL: 0 };
  const tickers = Array.isArray(t.tickers) ? t.tickers : [];

  const byTicker = tickers.map((tk) => {
    const mine = signals.filter((sg) => sg.ticker === tk.ticker);
    return {
      ...tk,
      aligns: mine.filter((sg) => sg.thesis_alignment === "ALIGNS").length,
      challenges: mine.filter((sg) => sg.thesis_alignment === "CHALLENGES").length,
      n: mine.length,
    };
  }).sort((a, b) => b.n - a.n);

  return (
    <Shell>
      {/* ── brand ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <NousIcon size={48} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.14em",
                        color: C.green }}>QUANT SIGNAL</div>
          <div style={{ fontSize: 10, letterSpacing: "0.22em", color: C.textDim }}>
            THESIS LEDGER
          </div>
        </div>
        {/* ⚠⚠ THE BADGE MEANS ONE THING: the signal data came from this platform
            rather than being self-reported. It is not a claim about the author,
            the thesis, or any return. There is no verification system behind it
            and the wording must never drift into implying one. */}
        <span style={{ fontSize: 10, fontWeight: 700, color: C.green,
                       border: `1px solid ${C.green}`, borderRadius: 3,
                       padding: "3px 9px", letterSpacing: "0.1em" }}>
          PLATFORM VERIFIED ✓
        </span>
      </div>

      {/* ── thesis ── */}
      <div style={{ marginTop: 22 }}>
        <div style={{ color: C.textMid }}>@{d.handle}</div>
        <div style={{ fontSize: 26, fontWeight: 700, color: C.green, lineHeight: 1.2,
                      margin: "4px 0 6px" }}>
          {t.thesis_name}
        </div>
        <div style={{ color: C.textDim }}>
          {String(t.direction || "").toUpperCase()} · {String(t.style || "").toUpperCase()}
          {" · "}{String(t.timeframe || "").toUpperCase()}
        </div>
        <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>
          Created {longDate(t.created_at)}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <span style={{
          fontSize: 15, fontWeight: 700, color, border: `1px solid ${color}`,
          borderRadius: 3, padding: "4px 12px", letterSpacing: "0.08em",
        }}>
          {label}
        </span>
        <div style={{ color: C.textDim, marginTop: 6, fontSize: 12 }}>
          {label === "WATCHING"
            ? "No signals yet — the platform is watching this basket."
            : `${counts.ALIGNS} aligning · ${counts.CHALLENGES} challenging · ${counts.NEUTRAL} neutral`}
        </div>
      </div>

      {/* ── the note ── */}
      {/* ⚠⚠ THE SEALED DATE IS THE POINT OF THIS SECTION. It is what makes the
          note evidence rather than commentary: the thinking is timestamped
          earlier than the outcomes below it, and nothing can edit it after.
          Omitted entirely when there is no note — an empty panel would read as
          an author who had nothing to say. */}
      {t.thesis_note && (
        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 11, color: C.textMid, letterSpacing: "0.12em" }}>
            THESIS NOTE
          </div>
          <div style={{ ...s.panel, padding: 16, marginTop: 8, whiteSpace: "pre-wrap",
                        lineHeight: 1.8 }}>
            {t.thesis_note}
          </div>
          <div style={{ fontSize: 11, color: C.textDim, marginTop: 6 }}>
            {t.note_updated_at && <>Written {longDate(t.note_updated_at)}</>}
            {t.note_locked_at && (
              <>
                {t.note_updated_at ? " · " : ""}
                <span style={{ color: C.green, fontWeight: 700 }}>
                  Sealed {longDate(t.note_locked_at)}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── signals ── */}
      <div style={{ fontSize: 11, color: C.textMid, letterSpacing: "0.12em",
                    margin: "22px 0 8px" }}>
        SIGNAL FEED
      </div>
      {signals.length === 0 ? (
        <div style={{ ...s.panel, padding: 16, color: C.textDim, lineHeight: 1.7 }}>
          No signals have fired against this thesis yet.
        </div>
      ) : (
        signals.map((sg) => (
          <SignalRow key={sg.id || `${sg.ticker}-${sg.detected_at}`}
                     sig={sg} windows={windows} />
        ))
      )}

      {/* ── breakdown ── */}
      {byTicker.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: C.textMid, letterSpacing: "0.12em",
                        margin: "22px 0 8px" }}>
            TICKER BREAKDOWN
          </div>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 6 }}>
            {byTicker.map((tk) => (
              <div key={tk.ticker} style={{ ...s.panel, padding: 10, minWidth: 150,
                                            flexShrink: 0 }}>
                <div style={{ fontWeight: 700 }}>{tk.ticker}</div>
                <div style={{ fontSize: 10, color: C.textDim, whiteSpace: "nowrap",
                              overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }}>
                  {SECTOR_ICONS[tk.sector] || ""} {tk.name || tk.sector || ""}
                </div>
                <div style={{ fontSize: 11, marginTop: 4 }}>
                  <span style={{ color: C.green }}>{tk.aligns}</span>
                  <span style={{ color: C.textDim }}> aligning · </span>
                  <span style={{ color: C.red }}>{tk.challenges}</span>
                  <span style={{ color: C.textDim }}> challenging</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── physical world ── */}
      {/* ⚠⚠ READS THE UNAUTHENTICATED ROUTE. /api/altdata/swim/flights is
          auth-gated and would 401 for every visitor this page exists for, so the
          panel is pointed at /api/thesis/public/swim-flights — same payload
          shape, same client-side filter, no account required.
          ⚠ No "view all flights" link: an anonymous reader has no tab shell to
          navigate into. The panel renders that button only when a navigator is
          supplied, so omitting the prop IS the control. */}
      <PhysicalWorldPanel
        sectors={t.sectors || []}
        endpoint="/api/thesis/public/swim-flights"
        footer={
          <a href="https://quant-signal.ai"
             style={{ display: "inline-block", marginTop: 6, fontSize: 11,
                      color: C.textMid, textDecoration: "none" }}>
            Powered by Quant Signal physical world intelligence →
          </a>
        }
      />

      {/* ── disclaimer ── */}
      <div style={{ ...s.panel, padding: 16, marginTop: 26, fontSize: 11,
                    color: C.textDim, lineHeight: 1.8 }}>
        This Alpha Ledger reflects signal intelligence accuracy only. Not
        investment returns. Not portfolio performance. Authored by the user.
        Signal data verified by Quant Signal. Past signal performance does not
        guarantee future results. Nothing on this page constitutes investment
        advice.
      </div>

      <Cta />
    </Shell>
  );
}

export default PublicLedger;
